<template>
  <div>
    <div class="mt-8">
      <v-row dense>
        <v-col
          cols="12"
          md="12"
          v-if="!isEditMode || sortedSessions.length == 0"
        >
          <div class="text-subtitle-1 font-weight-bold">Agenda</div>
        </v-col>

        <v-col cols="12" v-if="!isEditMode || sortedSessions.length == 0">
          <div>
            Spotlight your panel/speaker by adding their details to display in
            your EventConnect link. All event attendees that opt in to your
            EventConnect link, will be able to see and connect with speaker.
          </div>
        </v-col>
        <v-col cols="12" md="8">
          <v-btn
            :outlined="!isEditMode || sortedSessions.length == 0"
            depressed
            color="primary"
            large
            @click="handleAddSession"
            >+ Add a session</v-btn
          >
        </v-col>
        <v-col md="4" sm="12" v-if="sortedSessions.length > 0">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            outlined
            single-line
            hide-details
            placeholder="Search for a session"
            rounded
            dense
          ></v-text-field>
        </v-col>
      </v-row>
    </div>

    <v-data-table
      v-if="sortedSessions.length > 0"
      :headers="headers"
      :items="sortedSessions"
      class="mt-5"
      :items-per-page="8"
      :search="search"
      @click:row="handleEdit"
    >
      <template v-slot:item.date="{ item }">
        <div>
          {{ getSessionDate(item) }}
        </div>
      </template>
      <template v-slot:item.time="{ item }">
        <div>
          {{ getSessionTime(item) }}
        </div>
      </template>
      <template v-slot:item.type="{ item }">
        <div>
          {{ getSessionFormat(item) }}
        </div>
      </template>
      <template v-slot:item.speakers="{ item }">
        <div>
          {{ getNoOfSpeakersText(item) }}
        </div>
      </template>
      <!--  -->
      <template v-slot:item.action="{ item }">
        <v-menu offset-y left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on">
              <v-icon>more_vert</v-icon>
            </v-btn>
          </template>

          <v-list nav>
            <v-list-item @click="handleEdit(item)">
              <v-list-item-title>Edit session</v-list-item-title>
            </v-list-item>
            <v-list-item @click="handleDelete(item)">
              <v-list-item-title>Delete session</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>

    <ConfirmationDialog
      :show="showConfirmationDialog"
      :title="confirmationDialogTitle"
      :content="confirmationDialogContent"
      @click="deleteSession"
      @closeDialog="showConfirmationDialog = false"
      buttonText="Delete session"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Event from "@/models/Event";
import constants from "@/shared/constants";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import {onSnapshot, where} from "firebase/firestore";
import {queryEventCollection} from "@/shared/firebase_utils";

export default {
  data() {
    return {
      session: null,
      headers: [
        {
          text: "Session name",
          value: "name",
          align: "start",
        },
        {
          text: "Date",
          value: "date",
          align: "start",
        },
        {
          text: "Time",
          value: "time",
          align: "start",
        },
        {
          text: "Type",
          value: "type",
          align: "start",
        },
        {
          text: "Speakers",
          value: "speakers",
          align: "start",
        },
        { text: "", value: "action" },
      ],
      linkedSessions: [],
      unsubscribeSessionsSnapshot: null,
      search: "",
      sessionToBeDeleted: null,
      showConfirmationDialog: false,
      confirmationDialogTitle: "",
      confirmationDialogContent: "",
    };
  },
  computed: {
    ...mapGetters([
      "selectedBusinessProfileId",
      "businessProfile",
      "currentSubEvent",
      "currentEvent",
      "userProfile",
      "currentSession",
    ]),
    isEditMode() {
      return !this.currentSubEvent.isCreationInProgress;
    },
    sortedSessions() {
      let sortedArray = this.linkedSessions.sort(function (a, b) {
        // Turn your strings into dates, and then subtract them
        // to get a value that is either negative, positive, or zero.
        if (a.startDateTime.valueOf() == b.startDateTime.valueOf()) {
          if (a.eventModifiedAt && b.eventModifiedAt) {
            return new Date(a.eventModifiedAt) - new Date(b.eventModifiedAt);
          } else {
            return a.name.localeCompare(b.name);
          }
        } else {
          return new Date(a.startDateTime) - new Date(b.startDateTime);
        }
      });
      return sortedArray;
    },
  },
  components: {
    ConfirmationDialog,
  },
  created() {
    try {


      this.unsubscribeSessionsSnapshot = onSnapshot(
          queryEventCollection(
              where("businessProfileId", "==", this.userProfile.businessProfileId),
              where("parentSubEventId", "==", this.currentSubEvent.id)),
          (querySnapshot) => {
          const changes = querySnapshot.docChanges();
          changes.forEach((change) => {
            this.handleDocumentChange(change);
          });
        });
    } catch (error) {
      console.log(
        "Error occurred while fetching snapshot of events for super user: ",
        error
      );
    }
  },
  destroyed() {
    this.unsubscribeSessionsSnapshot();
  },
  methods: {
    ...mapActions(["setCurrentSession", "showSnackbar"]),
    async handleAddSession() {
      this.session = new Event({
        businessProfileId: this.selectedBusinessProfileId,
        hostName: this.businessProfile.businessName,
        isCreationInProgress: true,
        parentMultiConnectEventId: this.currentEvent.id,
        parentSubEventId: this.currentSubEvent.id,
        currentStep: "EVENT_DETAILS",
        linkPrivacy: this.currentEvent.linkPrivacy,
        eventCategory: constants.EVENT_CATEGORY.session
      });
      this.setCurrentSession(this.session);

      await this.currentSession.generateEventId();

      this.$router.push({
        name: "addEditSession",
        params: {
          eventId: this.currentEvent.id,
          subEventId: this.currentSubEvent.id,
          sessionId: this.currentSession.id,
        },
      });
    },
    handleEdit(session) {
      this.setCurrentSession(session);
      this.$router.push({
        name: "addEditSession",
        params: {
          eventId: this.currentEvent.id,
          subEventId: this.currentSubEvent.id,
          sessionId: session.id,
        },
      });
    },
    handleDocumentChange(change) {
      let addedSession;
      if (change.type === "added" || change.type == "modified") {
        //let data = change.doc.data({ serverTimestamps: "estimate" });
        addedSession = Event.fromFireStore(change.doc);
      }
      if (change.type === "added") {
        if (
          this.linkedSessions.length != 0 &&
          addedSession.createdAt > this.linkedSessions[0].createdAt
        ) {
          this.linkedSessions.unshift(addedSession);
        } else {
          this.linkedSessions.push(addedSession);
        }
      } else if (change.type == "modified") {
        console.log("modified");
        //Replace the old object within the array with the modified object
        this.linkedSessions.forEach((item, index) => {
          if (item.id == change.doc.id) {
            this.linkedSessions.splice(index, 1, addedSession);
          }
        });
      } else if (change.type == "removed") {
        //Remove the object from the array
        this.linkedSessions.forEach((item, index) => {
          if (item.id == change.doc.id) {
            this.linkedSessions.splice(index, 1);
          }
        });
      }
    },
    getSessionDate(subEvent) {
      return (
        new Intl.DateTimeFormat("en-US", {
          weekday: "long",
          timeZone: this.currentEvent.timezone,
        }).format(subEvent.startDateTime) +
        ", " +
        new Intl.DateTimeFormat("en-US", {
          dateStyle: "medium",
          timeZone: this.currentEvent.timezone,
        }).format(subEvent.startDateTime)
      );
    },
    getSessionTime(subEvent) {
      return (
        new Intl.DateTimeFormat("en-US", {
          timeStyle: "short",
          timeZone: this.currentEvent.timezone,
        }).format(subEvent.startDateTime) +
        " - " +
        new Intl.DateTimeFormat("en-US", {
          timeStyle: "short",
          timeZone: this.currentEvent.timezone,
        }).format(subEvent.endDateTime)
      );
    },
    getSessionFormat(session) {
      if (session.type == "in-person") return "In-person";
      else if (session.type == "online") return "Online";
      else return "Hybrid";
    },
    getNoOfSpeakersText(session) {
      let noOfSpeakers = this.getNoOfSpeakers(session);
      if (noOfSpeakers == 0) return "-";
      else if (noOfSpeakers == 1) return "1 speaker";
      else return noOfSpeakers + " speakers";
    },
    handleDelete(session) {
      this.sessionToBeDeleted = session;
      this.confirmationDialogTitle =
        "Delete " + this.sessionToBeDeleted.name + "? ";

      if (this.getNoOfSpeakers(this.sessionToBeDeleted) >= 1) {
        this.confirmationDialogContent =
          "This session and speakers will be removed from the program listing in the app.";
      } else {
        this.confirmationDialogContent =
          "This session will be removed from the program listing in the app.";
      }
      this.showConfirmationDialog = true;
    },
    async deleteSession() {
      try {
        //Delete the session
        this.sessionToBeDeleted.remove();

        //Update linked sessions for the parent sub event
        this.currentSubEvent.linkedSessions =
          this.currentSubEvent.linkedSessions.filter(
            (sessionId) => sessionId != this.sessionToBeDeleted.id
          );

        //Save sub-event
        this.currentSubEvent.save();
      } catch (error) {
        console.log("Error occurred while deleting session: ", error);
      }

      //Start: Delete the speakers associated with this session from the event
      if (this.getNoOfSpeakers(this.sessionToBeDeleted) >= 1) {
        console.log("removing speakers...");
        //Delete speakers and the session
        let speakers = this.getSpeakers(this.sessionToBeDeleted);
        await Promise.all(
          speakers.map(async (speaker) => {
            console.log("speaker: ", speaker.uid);

            await this.currentEvent.removeSpeaker(
              speaker,
              this.sessionToBeDeleted,
              this.sessionToBeDeleted.name
            );
          })
        );
      }
      //End: Delete the speakers associated with this session from the event

      //Start: Remove session from this list if they were removed by the user
      let removedSessionIndex = this.linkedSessions.findIndex(
        (session) => session.id == this.sessionToBeDeleted.id
      );
      this.linkedSessions.splice(removedSessionIndex, 1);
      //End: Remove speaker from this list if they were removed by the user

      //Hide the dialog
      this.showConfirmationDialog = false;

      this.showSnackbar({ show: true, text: "Session deleted successfully!" });
    },
    getNoOfSpeakers(session) {
      let speakers = this.getSpeakers(session);
      return speakers.length;
    },
    getSpeakers(session) {
      let speakers = this.currentEvent.speakers.filter(
        (speaker) =>
          speaker.associatedSpeakerSessions.length > 0 &&
          speaker.associatedSpeakerSessions.includes(session.id)
      );
      return speakers;
    },
  },
};
</script>

<style>
</style>